import React from 'react';
import { ScaleLinear } from 'd3-scale';
import styles from './MapChartLegend.module.scss';
import { memoize } from 'lodash';

export const formatter = memoize((config = { useGrouping: false, maximumFractionDigits: 0 }) =>
  Intl.NumberFormat(undefined, config),
);

const suffixes = ['k', 'M', 'B', 'T'];
const calcSuffix = (input: number) => {
  let value = input;
  let suffix = '';
  for (let i = 0, j = suffixes.length; i < j && Math.abs(value) >= 1e3; i += 1) {
    value /= 1000;
    suffix = suffixes[i];
  }

  return { value, suffix };
};

/**
 * Converts values to the units of the appropriate numeric suffix.
 * This applies to numbers < -1k && > 1k.
 * @param  {Number} number
 * @return {String}
 */
const applyMetricSuffix = (input: number) => {
  const { value, suffix } = calcSuffix(input);
  const formatted =
    Math.abs(input) > 1e6
      ? formatter({ useGrouping: false, maximumFractionDigits: 2 }).format(value)
      : formatter({ useGrouping: false, maximumFractionDigits: 1 }).format(value);
  const output = `${formatted}${suffix}`;

  return output;
};

const BLOCK_NAME = 'map_chart_legend';

const SPOTIFY_NA = 'Spotify N/A';

type Props = {
  scale: ScaleLinear<number, number> | ScaleLinear<string, string>;
  height: number;
};

export function MapChartLegend(props: Props) {
  const { scale, height } = props;

  const { 0: domainMin, 1: domainMax } = scale.domain();

  let ticks = [domainMin, domainMax * (1 / 3), domainMax * (2 / 3), domainMax];

  const difference = domainMax - domainMin;

  if (difference === 2) {
    // less than four unique ticks
    ticks = [domainMin, domainMax / 2, domainMax];
  } else if (difference === 1) {
    // less than three unique ticks
    ticks = [domainMin, domainMax];
  } else if (difference === 0) {
    // less than two unique ticks
    ticks = [domainMin];
  } else if (domainMax == null || isNaN(domainMax)) {
    ticks = [];
  }

  return (
    <div className={styles[BLOCK_NAME]} style={{ height }}>
      <div className={styles[`${BLOCK_NAME}-gradient-container`]}>
        <div className={styles[`${BLOCK_NAME}-gradient`]} />
        <div className={styles[`${BLOCK_NAME}-ticks`]}>
          {ticks.map((d, i) => {
            let tickText;
            const tickIndex = i;
            if (d < 1 && d !== 0) {
              tickText = '';
            } else if (d < 100) {
              tickText = Math.round(d);
            } else if (d < 999500) {
              tickText = applyMetricSuffix(Math.round(d / 1000) * 1000);
            } else {
              tickText = applyMetricSuffix(Math.round(d / 1000000) * 1000000);
            }
            return (
              <div
                key={`${d}-${tickIndex}`}
                className={styles[`${BLOCK_NAME}-tick`]}
                style={{
                  left: `${100 * (tickIndex / (ticks.length - 0.75))}%`,
                }}
              >
                {tickIndex === ticks.length - 1 ? `${tickText}+` : tickText}
              </div>
            );
          })}
        </div>
      </div>
      <div className={styles[`${BLOCK_NAME}-swatch-container`]}>
        <div className={styles[`${BLOCK_NAME}-swatch`]} />
        <div className={styles[`${BLOCK_NAME}-label-container`]}>
          <div className={styles[`${BLOCK_NAME}-label`]}>{SPOTIFY_NA}</div>
        </div>
      </div>
    </div>
  );
}
